import { useState } from 'react';
import { useT } from '../i18n';
import { errorMessage, post } from '../lib/api';
import { useSession } from '../state/session';
import { Dialog } from './Dialog';
import { Notice } from './Notice';

export function formatPrice(amount: number, currency: string, locale = 'pt-BR'): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
}

interface Props {
  open: boolean;
  /** Shown after the trial ended: explains why speech is disabled. */
  trialEnded: boolean;
  onClose(): void;
}

export function UpgradeDialog({ open, trialEnded, onClose }: Props) {
  const t = useT();
  const { config } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const billing = config?.billing;
  const price = billing?.price;

  async function checkout() {
    setBusy(true);
    setError(null);
    try {
      const { url } = await post<{ url: string }>('/api/billing/checkout');
      window.location.assign(url);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={trialEnded ? t('trialEndedTitle') : t('upgradeTitle')}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            {t('notNow')}
          </button>
          {billing?.enabled && (
            <button type="button" className="btn btn--primary" onClick={() => void checkout()} disabled={busy}>
              {busy ? t('loading') : t('subscribeNow')}
            </button>
          )}
        </>
      }
    >
      <p>{trialEnded ? t('trialEndedText') : t('upgradeText')}</p>
      {price && (
        <p className="price">
          <strong>{formatPrice(price.amount, price.currency)}</strong>
          <span className="muted">{t('perMonth')}</span>
        </p>
      )}
      {!billing?.enabled && <Notice kind="info">{t('billingDisabled')}</Notice>}
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}
